import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const createSession = async (email) => {
  const res = await axios.post(`${API}/sessions`, { email });
  return res.data;
};

export const getSession = async (email) => {
  const res = await axios.get(`${API}/sessions/${encodeURIComponent(email)}`);
  return res.data;
};

export const updateSession = async (sessionId, data) => {
  const res = await axios.put(`${API}/sessions/${sessionId}`, data);
  return res.data;
};

export const createAssessment = async (data) => {
  const res = await axios.post(`${API}/assessments`, data);
  return res.data;
};

export const getAssessment = async (email) => {
  const res = await axios.get(`${API}/assessments/${encodeURIComponent(email)}`);
  return res.data;
};

// Load an editable assessment by its id (used by invited contributors whose
// email doesn't match the owner-scoped getAssessment lookup).
// Owner-only final submission. Backend rejects non-owner with 403.
export const submitAssessment = async (assessmentId, actingEmail) => {
  const res = await axios.post(`${API}/assessments/${assessmentId}/submit`, {
    acting_email: actingEmail,
  });
  return res.data;
};

export const getAssessmentById = async (assessmentId) => {
  const res = await axios.get(`${API}/assessments/by-id/${assessmentId}`);
  return res.data;
};

export const listAssessmentsByEmail = async (email) => {
  const res = await axios.get(`${API}/assessments/by-email/${encodeURIComponent(email)}/list`);
  return res.data;
};

export const updateAssessment = async (assessmentId, data) => {
  const res = await axios.put(`${API}/assessments/${assessmentId}`, data);
  return res.data;
};

// --- Contributors / collaborators (phase 1 — data model only) ---
export const listCollaborators = async (assessmentId, actingEmail) => {
  const res = await axios.get(`${API}/assessments/${assessmentId}/collaborators`, {
    params: { acting_email: actingEmail },
  });
  return res.data; // { collaborators: [...] }
};

export const addCollaborator = async (assessmentId, email, actingEmail) => {
  const res = await axios.post(`${API}/assessments/${assessmentId}/collaborators`, {
    email, acting_email: actingEmail,
  });
  return res.data; // { collaborators: [...] }
};

export const setCollaboratorSections = async (assessmentId, email, assignedSections, actingEmail, assignedGroups = []) => {
  const res = await axios.put(
    `${API}/assessments/${assessmentId}/collaborators/${encodeURIComponent(email)}/sections`,
    { assigned_sections: assignedSections, assigned_groups: assignedGroups, acting_email: actingEmail }
  );
  return res.data; // { collaborators: [...] }
};

export const sendCollaboratorInvites = async (assessmentId, actingEmail) => {
  const res = await axios.post(`${API}/assessments/${assessmentId}/collaborators/send-invite`, {
    acting_email: actingEmail,
  });
  return res.data; // { total, sent, failed:[...], collaborators:[...] }
};

export const removeCollaborator = async (assessmentId, email, actingEmail) => {
  const res = await axios.delete(
    `${API}/assessments/${assessmentId}/collaborators/${encodeURIComponent(email)}`,
    { params: { acting_email: actingEmail } }
  );
  return res.data; // { collaborators: [...] }
};

export const trackEvent = async (eventType, email = null, metadata = {}) => {
  try {
    await axios.post(`${API}/events`, {
      event_type: eventType,
      email,
      metadata,
    });
  } catch (e) {
    console.error("Event tracking failed:", e);
  }
};

export const getQuestionsGrouped = async (module) => {
  const res = await axios.get(`${API}/questions/${module}/grouped`);
  return res.data;
};

// Admin-driven evaluation-type visibility. A question shows for a scenario when
// its `evaluation_types` contains that scenario. Missing/empty evaluation_types
// on legacy questions is treated as ["greenfield","brownfield"] (preserves old
// behavior). When no scenario is selected yet, nothing is filtered out.
export const questionAppliesToScenario = (q, scenario) => {
  if (!scenario) return true;
  const types = Array.isArray(q?.evaluation_types) && q.evaluation_types.length
    ? q.evaluation_types
    : ["greenfield", "brownfield"];
  // Mixed Landscape keeps enterprise-level landscape questions (those applying
  // to greenfield or brownfield). Scenario-specific details are captured per
  // Warehouse Group in Step 2 rather than globally.
  if (scenario === "mixed_landscape") {
    return types.includes("greenfield") || types.includes("brownfield");
  }
  return types.includes(scenario);
};

export const listQuestionsPublic = async () => {
  const res = await axios.get(`${API}/questions`);
  return res.data;
};

// Admin-driven answer options for specific question field_keys (regardless of
// active status). Used by the Warehouse Group editor so option lists stay
// sourced from the question bank rather than hardcoded in the frontend.
export const getQuestionFieldOptions = async (keys) => {
  const q = Array.isArray(keys) ? keys.join(",") : keys;
  const res = await axios.get(`${API}/questions/field-options`, { params: { keys: q } });
  return res.data; // { field_key: { answer_type, answer_options, max_selections } }
};

// --- Demo Mode (public) ---
export const getDemoMode = async () => {
  const res = await axios.get(`${API}/demo-mode`, { withCredentials: true });
  return res.data; // { enabled, role, profiles: [...] }
};

export const getDemoProfile = async (profileId) => {
  const res = await axios.get(`${API}/demo-profiles/${profileId}`, { withCredentials: true });
  return res.data;
};

// --- SmartImprove Demo Profiles (public, gated by admin/demo role) ---
export const listSmartImproveDemoProfiles = async () => {
  const res = await axios.get(`${API}/smartimprove/demo-profiles`, {
    withCredentials: true,
  });
  return res.data;
};

export const applySmartImproveDemoProfile = async (profileId) => {
  const res = await axios.post(
    `${API}/smartimprove/demo-profiles/${profileId}/apply`,
    null,
    { withCredentials: true },
  );
  return res.data; // { engagement_id, company_name, site_count, finding_count }
};

// Apply an edited SmartImprove demo payload (used by the intake page's
// final submit when in demo-prefill mode). Creates engagement + sites +
// findings atomically with status='accepted'. No AI generation.
export const applySmartImproveDemoFromPayload = async (payload) => {
  const res = await axios.post(
    `${API}/smartimprove/demo-profiles/apply-from-payload`,
    payload,
    { withCredentials: true },
  );
  return res.data;
};
